/**
 * Math Utilities
 *
 * Simple statistical functions used across zone analysis.
 */

/** Calculate the average of an array of numbers */
export function average(values: number[]): number {
  if (values.length === 0) return 0
  return values.reduce((a, b) => a + b, 0) / values.length
}

/** Calculate the standard deviation of an array of numbers */
export function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0
  const mean = average(values)
  const squaredDiffs = values.map((v) => Math.pow(v - mean, 2))
  return Math.sqrt(average(squaredDiffs))
}

/**
 * Returns the median of three values.
 * Used as a 3-point median filter to eliminate single-point GPS noise spikes
 * while preserving sustained acceleration/braking values.
 */
export function median3(a: number, b: number, c: number): number {
  if (a <= b) return b <= c ? b : Math.max(a, c)
  return a <= c ? a : Math.max(b, c)
}

/**
 * Returns the median of five values.
 * Handles bursts of up to 2 consecutive GPS noise spikes.
 */
export function median5(
  a: number,
  b: number,
  c: number,
  d: number,
  e: number
): number {
  const arr = [a, b, c, d, e].sort((x, y) => x - y)
  return arr[2]
}

/**
 * Returns the median of seven values.
 * Handles bursts of up to 3 consecutive GPS noise spikes.
 */
export function median7(
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  f: number,
  g: number
): number {
  const arr = [a, b, c, d, e, f, g].sort((x, y) => x - y)
  return arr[3]
}

/**
 * Returns the median of N values (general).
 */
export function medianN(...values: number[]): number {
  values.sort((x, y) => x - y)
  return values[Math.floor(values.length / 2)]
}

/**
 * Returns a smoothed acceleration value using a 9-point median filter.
 * Falls back to 7-point, 5-point, 3-point, or raw value at edges.
 *
 * At 10Hz, a 9-point window covers 0.9s. Real braking peaks last 2-4s,
 * so genuine sustained peaks are fully preserved. GPS multipath noise
 * (correlated errors lasting 0.3-0.5s = 3-5 consecutive samples) is filtered.
 */
export function smoothedAcceleration(
  data: { acceleration: number }[],
  index: number
): number {
  let raw: number
  if (index >= 4 && index < data.length - 4) {
    // Full 9-point median window available
    raw = medianN(
      data[index - 4].acceleration,
      data[index - 3].acceleration,
      data[index - 2].acceleration,
      data[index - 1].acceleration,
      data[index].acceleration,
      data[index + 1].acceleration,
      data[index + 2].acceleration,
      data[index + 3].acceleration,
      data[index + 4].acceleration
    )
  } else if (index >= 3 && index < data.length - 3) {
    raw = median7(
      data[index - 3].acceleration,
      data[index - 2].acceleration,
      data[index - 1].acceleration,
      data[index].acceleration,
      data[index + 1].acceleration,
      data[index + 2].acceleration,
      data[index + 3].acceleration
    )
  } else if (index >= 2 && index < data.length - 2) {
    raw = median5(
      data[index - 2].acceleration,
      data[index - 1].acceleration,
      data[index].acceleration,
      data[index + 1].acceleration,
      data[index + 2].acceleration
    )
  } else if (index >= 1 && index < data.length - 1) {
    raw = median3(
      data[index - 1].acceleration,
      data[index].acceleration,
      data[index + 1].acceleration
    )
  } else {
    raw = data[index].acceleration
  }
  return raw
}
